import { useEffect, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  createCardDraft,
  cardDraftHasChanges,
  racquetCardDraftKey
} from './racquetCardDraft'

const memoryDrafts = new Map()

// Drafts are independent of the server projection and editor mount lifetime.
// Never move a draft's base revision forward merely because a GET/ack arrived.
export function useRacquetCardDraft(
  clubCode,
  matchId,
  match,
  queueSnapshot,
  retryEvent
) {
  const { user } = useAuth0()
  const key = racquetCardDraftKey({
    clubCode,
    memberIdentity: user?.sub || '',
    matchId
  })
  const [saved, setSaved] = useState(null),
    [storageError, setStorageError] = useState(false),
    [error, setError] = useState('')
  const unsafeStorage = useRef(false)
  unsafeStorage.current = storageError
  const current = useRef({ key, draft: null }),
    loaded = useRef(null)
  if (current.current.key !== key) current.current = { key, draft: null }
  const draft = saved?.key === key ? saved.draft : null
  const write = (value) => {
    try {
      if (value) localStorage.setItem(key, JSON.stringify(value))
      else localStorage.removeItem(key)
      if (value) memoryDrafts.set(key, value)
      else memoryDrafts.delete(key)
      current.current = { key, draft: value }
      setSaved({ key, draft: value })
      setStorageError(false)
      return true
    } catch {
      if (value) {
        memoryDrafts.set(key, value)
        current.current = { key, draft: value }
        setSaved({ key, draft: value })
      }
      setStorageError(true)
      return false
    }
  }
  useEffect(() => {
    loaded.current = key
    try {
      const memory = memoryDrafts.get(key)
      if (memory) {
        write(memory)
        setError('')
        return
      }
      const raw = localStorage.getItem(key),
        value = raw ? JSON.parse(raw) : null
      if (
        value &&
        (value.version !== 1 ||
          !Number.isInteger(value.baseRevision) ||
          !Array.isArray(value.rows) ||
          value.rows.length > 101 ||
          value.rows.some(
            (row) =>
              !Array.isArray(row.score) ||
              row.score.length !== 2 ||
              row.score.some((v) => typeof v !== 'string') ||
              typeof row.completed !== 'boolean'
          ) ||
          !Array.isArray(value.points) ||
          value.points.length !== 2 ||
          value.points.some((v) => typeof v !== 'string') ||
          !['ordinary', 'tieBreak'].includes(value.pointMode) ||
          (value.pendingEvent &&
            value.pendingEvent.id !== value.pendingEventId))
      )
        throw new Error('draft_unreadable')
      current.current = { key, draft: value }
      setSaved({ key, draft: value })
      setStorageError(false)
      setError('')
    } catch {
      setStorageError(true)
      setError('draft_unreadable')
    }
  }, [key])
  useEffect(() => {
    const warn = (e) => {
      if (current.current.draft && unsafeStorage.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [])
  useEffect(() => {
    if (!draft?.pendingEventId || !match) return
    const accepted = match.events.some((e) => e.id === draft.pendingEventId)
    const queued = queueSnapshot?.events.some(
      (e) => e.id === draft.pendingEventId
    )
    if (accepted && !queued && !queueSnapshot?.storageError) write(null)
    else if (
      !accepted &&
      !queued &&
      queueSnapshot &&
      !queueSnapshot.blocked &&
      draft.pendingEvent &&
      match.revision === draft.pendingEvent.expectedRevision
    ) {
      try {
        retryEvent(draft.pendingEvent)
      } catch (e) {
        setError(e.code || 'draft_revision_changed')
      }
    }
  }, [match, queueSnapshot, draft?.pendingEventId, key])
  useEffect(() => {
    if (
      draft &&
      match &&
      !cardDraftHasChanges(draft, match) &&
      (draft.baseRevision !== match.revision ||
        (draft.baseEventId ?? null) !== (match.events.at(-1)?.id || null))
    )
      write(null)
  }, [match, draft, key])
  const change = (update) => {
    if (loaded.current !== key || !match || error === 'draft_unreadable') return
    const previous = current.current.draft || createCardDraft(match)
    if (previous.pendingEventId) return
    write({
      ...previous,
      ...(typeof update === 'function' ? update(previous) : update)
    })
    setError('')
  }
  return {
    scopeKey: key,
    draft: match ? draft : null,
    values: match ? draft || createCardDraft(match) : null,
    dirty:
      Boolean(match && cardDraftHasChanges(draft, match)) ||
      error === 'draft_unreadable',
    storageError,
    error,
    setError,
    change,
    replace: (value) => write(value),
    discard: () => {
      const removed = write(null)
      if (removed) setError('')
      return removed
    },
    queued: (event) => {
      const value = current.current.draft
      if (value)
        write({ ...value, pendingEventId: event.id, pendingEvent: event })
    },
    resolveAfterQueueReview: () => {
      const value = current.current.draft
      if (value) {
        const { pendingEvent, pendingEventId, ...entries } = value
        write({ ...entries, reconcileRevision: null })
      }
    },
    retryStorage: () => write(current.current.draft)
  }
}
